/**
 * 需要想定 daily 画面の page-model (アーキ改善 C3)。
 *
 * 従来 index.tsx の render body に散っていた「決定ロジック」を単一の deep な
 * hook に集約する:
 * - 編集 selection state (targetScope / selectedRetailerBusinessCd / dragMode) と
 *   未保存ガード経由の切替
 * - gate 群 (`deriveForecastGates` / `deriveRetailerScope`、pure fn へ委譲)
 * - 過去日 write-seam の強制 (canMutate を index から撤去し hook 内に閉じる)
 * - save / dirty の BG_TOTAL fallback
 * - write orchestration (保存 / 保存して移動 / 確定 / 衝突解決 / 破棄して移動)
 * - 確定ダイアログ・rescale モーダルの UI 状態
 *
 * editor (`useForecastDraftEditor`) / 離脱ガード (`useUnsavedGuard`) /
 * 確定 mutation (`useConfirmDemandForecast`) を配下に compose する。index は
 * input を解決してこの hook を呼び、返り値を view へ配線するだけになる
 * (deletion test: 本 hook を消すと決定が index に再集中する)。
 *
 * 過去日 write-gate: canMutate は index から撤去し、本 hook 内の 3 つの controlled
 * 箇所にのみ閉じる (旧: index の 4 seam に分散していた convention 依存を排除):
 * - 表示 gate: `canMutateTargetDate` flag (毎レンダー実時刻で再評価)。index は
 *   この flag のみ消費する (write-seam と表示 gate の分離を維持)。
 * - write orchestration 入口 (保存 / 保存して移動) は `guardWrite` HOF 経由で構築し
 *   過去日を no-op / cancel にする。write-gate の primary constructor。
 * - 確定 (`handleConfirmExecute`) は areaCode/targetDate の precondition を先に
 *   評価する順序制約があるため guardWrite ではなく同一 predicate を inline で呼ぶ
 *   (この 1 箇所だけ HOF ではなく inline、順序制約の帰結)。
 * なお破棄して移動 (`handleDiscardAndLeave`) と衝突解決 (`handleResolveConflict`) は
 * 値変更を伴わないため意図的に unguarded。editor 内部 (`canMutateNow`) は buffer
 * 書込の二重防御としてそのまま併存する。
 */
import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { DemandForecastDailyPage } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import {
  ConfirmDemandForecastRequestSchema,
  DemandForecastConfirmErrorReason,
  DemandForecastConfirmErrorReasonSchema,
  RetailerConfirmTargetSchema,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard'
import { readAppErrorInfo } from '~/libs/appErrorInfo'
import { appToaster } from '~/libs/toaster'
import { now as serverNow } from '~/models/clock'
import { useConfirmDemandForecast } from '~/pages/DemandForecastDaily/hooks/useConfirmDemandForecast'
import type { SaveAllResult } from '~/pages/DemandForecastDaily/hooks/useDraftSaveQueue'
import type { ForecastEditorViewModel } from '~/pages/DemandForecastDaily/hooks/useForecastDraftEditor'
import { useForecastDraftEditor } from '~/pages/DemandForecastDaily/hooks/useForecastDraftEditor'
import { canMutate } from '~/pages/DemandForecastDaily/models/canMutate'
import type { ForecastGates } from '~/pages/DemandForecastDaily/models/forecastGates'
import {
  deriveForecastGates,
  deriveRetailerScope,
} from '~/pages/DemandForecastDaily/models/forecastGates'
import { formatProtoDate } from '~/pages/DemandForecastDaily/models/sectionHelpers'
import {
  collectNegativeKomas,
  formatLimitViolationLines,
  formatNegativeWarning,
} from '~/pages/DemandForecastDaily/models/slotValueLimit'
import type { TargetScope } from '~/pages/DemandForecastDaily/models/types'

/** 確定ダイアログが submit する理由テキスト群 (ConfirmDemandDialog onConfirm)。 */
export type ConfirmRationale = {
  curveRationale?: string
  maxPowerRationale?: string
  minPowerRationale?: string
  numericalBasis?: string
  freeNote?: string
}

export type UseForecastPageModelParams = {
  page: DemandForecastDailyPage | undefined
  caseId: string | undefined
  selectedAreaCode: AreaCode | undefined
  section: WorkflowSection
  targetDate: ProtoDate | undefined
  gcKoma: number
  /** BG 全体ビューの配下小売一覧 (page?.retailers ?? [])。 */
  retailers: DemandForecastDailyPage['retailers']
  /** read-only (需要想定確定 / 完了 / 削除済み)。 */
  isCompleted: boolean
  /** wfCase が需要想定の確定と確定日時を返している。 */
  isConfirmedOnServer: boolean
  refetchPage: () => Promise<unknown>
}

export type UseForecastPageModelResult = ForecastGates & {
  // 配下 editor (`useForecastDraftEditor`) を view 向け projection
  // (`ForecastEditorViewModel`) に絞った単一 nested value として公開する。従来は
  // editor の内部形状 (19 field) を本 hook 自身の interface に Pick で素通し
  // 再エクスポートしており、editor の result 型が変わると public interface も churn
  // していた (seam leakage)。projection 型を editor 側に co-locate することで、
  // editor の field rename は projection の更新で閉じ、本 interface
  // (editor: ForecastEditorViewModel の 1 行) は churn しない。かつ view には
  // 表示 state / buffer-write ハンドラだけを見せ、page-model が wrap している
  // raw write 入口 (executeSaveAll 等) / fallback 前の raw 値 (saveStates 等) は
  // 到達不能に保つ (orchestration 迂回を型で塞ぐ)。index は model.editor から読む。
  editor: ForecastEditorViewModel
  // selection state
  targetScope: TargetScope
  selectedRetailerBusinessCd: string | undefined
  dragMode: 0 | 1
  setDragMode: Dispatch<SetStateAction<0 | 1>>
  onRetailerChange: (retailerBusinessCd: string) => void
  onTargetScopeChange: (scope: TargetScope) => void
  // scope
  isSingleRetailerBg: boolean
  hasMultipleRetailers: boolean
  canMutateTargetDate: boolean
  /** 対象日の表示ラベル (YYYY/MM/DD)。保存・確定の文言と画面表示で共有する。 */
  targetDateLabel: string
  // write orchestration
  handleSave: () => void
  handleSaveAndLeave: () => Promise<void>
  handleDiscardAndLeave: () => void
  handleResolveConflict: () => void
  handleConfirmExecute: (rationale: ConfirmRationale) => Promise<void>
  // leave guard
  leaveGuardBlocked: boolean
  leaveGuardCancel: () => void
  isLeaveSaving: boolean
  // confirm dialog + result
  isConfirmOpen: boolean
  openConfirmDialog: () => void
  closeConfirmDialog: () => void
  isConfirming: boolean
  confirmError: ReturnType<typeof useConfirmDemandForecast>['error']
  confirmResultAt: Date | undefined
  closeConfirmResult: () => void
  confirmedIndicatorAt: Date | undefined
  hasUnattributedRemaining: boolean
  isUnattributedGuardOpen: boolean
  closeUnattributedGuard: () => void
  // rescale modal
  isRescaleOpen: boolean
  openRescale: () => void
  closeRescale: () => void
}

export function useForecastPageModel(
  params: UseForecastPageModelParams,
): UseForecastPageModelResult {
  const {
    page,
    caseId,
    selectedAreaCode,
    section,
    targetDate,
    gcKoma,
    retailers,
    isCompleted,
    isConfirmedOnServer,
    refetchPage,
  } = params

  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  // ── 編集 selection state ──────────────────────────────────────────
  //
  // **「対象」は AC-SC-031 (フィルタ選択状態の localStorage 保持) の対象に含めない。**
  // `shared-components.md` 機能詳細 5 が「フィルタバーを持つ画面は AC-SC-031 により
  // 保持を必須とする (画面個別の spec で改めて規定しない)」とするため、 除外の判断は
  // ここに残す。
  //
  // - **これは表示の絞り込みではなく編集対象の選択**で、 値によって破壊的操作の可否が
  //   変わる: 48 コマ直接編集・グラフ変更・最大電力補正 (AC-DFD-004c/004d)、
  //   簡易予測の一括取込 (AC-DFD-033/033a)、 参照列の採用 (AC-DFD-047)。
  //   AC-DFD-041 は BG 切替を「ページ離脱・タブ切替・断面切替」と同列の離脱イベントと
  //   して未保存 3 択ダイアログの対象にしており (実装も `leaveGuard.guardAction()`
  //   経由)、 AC-SC-031 の User Story「フィルタボタンで表示データを絞り込みたい」が
  //   指す対象ではない
  // - 同じ画面に**失敗コストの大きい選択は都度既定へ復帰させる**確立済みの判断がある。
  //   AC-DFC-008b は気象観測地点の一時切替を別画面移動で組織共通設定へ戻す。理由も
  //   spec 本文に明記されている (「意図せず別地点のまま別断面の想定を進めない」)。
  //   既定の BG 全体は配下複数なら直接編集不可 (AC-DFD-004d) = 安全側で、 保持すると
  //   再訪時にいきなり**編集可能な小売スコープ**で開く
  // - 候補 (retailers) はケース・対象日ごとの応答由来で、 対象日時点で失効・適用前の
  //   BG / 小売は選択肢から外れる (AC-DFC-001)。 候補外コードを復元すると select は
  //   空表示なのに本体は黙って BG 合計へフォールバックする
  const [targetScope, setTargetScope] = useState<TargetScope>('BG_TOTAL')
  const [selectedRetailerBusinessCd, setSelectedRetailerBusinessCd] = useState<
    string | undefined
  >(undefined)
  // 補正モード (design L742-756): 0=編集コマのみ変更, 1=編集コマ以降へ比例配分。
  const [dragMode, setDragMode] = useState<0 | 1>(0)

  // AC-004c/004d: 表示用に BG 全体ビューと小売数を組み合わせる。
  const { isSingleRetailerBg, hasMultipleRetailers } = deriveRetailerScope(
    targetScope,
    retailers,
  )

  // AC-DFD-054a: 集計表示で内訳の合計が 0 のコマ (全社 0、または正負が打ち消し合って 0)
  // を変えようとした時の通知 (2026-10-13 PdM 判断で文言を「合計が 0」に統一)。
  const handleAggregateZeroRejected = useCallback((): void => {
    appToaster.create({
      type: 'warning',
      title: t('page:demandForecastDaily.koma.aggregateZeroRejected'),
    })
  }, [t])

  // ── 編集グリッド中核ロジック (editor hook を配下に compose) ──────────
  const draftEditor = useForecastDraftEditor({
    page,
    caseId,
    selectedAreaCode,
    section,
    targetDate,
    selectedRetailerBusinessCd,
    targetScope,
    gcKoma,
    dragMode,
    refetchPage,
    onAggregateZeroRejected: handleAggregateZeroRejected,
  })

  // orchestration (gate 派生 / write / leaveGuard / confirm) が読む editor 内部値
  // だけを取り出す。表示 state / buffer-write ハンドラは素通しせず draftEditor を
  // 丸ごと editor として公開する (return の editor: draftEditor)。
  const {
    editRows,
    hasUnattributedRemaining,
    effectiveWriteTarget,
    aggregateEdit,
    unattributedSlots,
    unattributedBaselineSlots,
    saveStates,
    bgTotalLiveSlots,
    currentEditRow,
    currentDirtyKomas,
    currentRowDirty,
    bgTotalDirtyKomas,
    anyDirty,
    anyConflict,
    currentSaveState,
    executeSaveAll,
    discardAllDirty,
    resolveConflicts,
  } = draftEditor
  // 過去日 write-gate の表示側 flag。canMutate は実時刻 (now()) を読むため毎レンダー
  // 評価する (useMemo しない: 画面を開いたまま JST 0:00 を跨いだ直後の再レンダーで
  // 過去日へ即時追従させる、useJstMidnightTick と対)。
  const canMutateTargetDate = canMutate(targetDate)

  // ── gate 群 (pure fn へ委譲、mount 無しで unit-addressable) ───────────
  const gates = deriveForecastGates({
    targetScope,
    hasUnattributedRemaining,
    isRetailerEditableTarget: effectiveWriteTarget.scope === 'RETAILER',
    aggregateEdit,
    unattributedSlots,
    unattributedBaselineSlots,
    bgTotalLiveSlots,
    currentEditRow,
    editRows,
    isCompleted,
    canMutateTargetDate,
    gcKoma,
    currentDirtyKomas,
    currentRowDirty,
    bgTotalDirtyKomas,
    anyDirty,
    currentSaveState,
    anyConflict,
  })

  // ── 未保存離脱ガード ──────────────────────────────────────────────
  const leaveGuard = useUnsavedGuard({
    isDirty: anyDirty,
    shouldBlockAction: () => anyDirty,
  })
  const [isLeaveSaving, setIsLeaveSaving] = useState(false)

  // ── 過去日 write-seam の単一 constructor ─────────────────────────────
  // write orchestration 入口 (保存 / 保存して移動) はこの HOF 経由でのみ構築し、
  // canMutate(targetDate) の呼び出し口を本 hook 内の 1 箇所に閉じる (旧: index の
  // 4 seam に分散していた convention 依存を排除)。canMutate は返す closure 内で
  // 呼ぶため実行時の実時刻で評価される (日跨ぎ残存経路対策)。
  const guardWrite = useCallback(
    (fn: () => void | Promise<void>, onBlocked?: () => void) =>
      async (): Promise<void> => {
        if (targetScope === 'UNATTRIBUTED' || !canMutate(targetDate)) {
          onBlocked?.()
          return
        }
        await fn()
      },
    [targetDate, targetScope],
  )

  const targetDateLabel = formatProtoDate(targetDate)

  // AC-DFC-EDIT-005: 負の値のコマを含めて保存・確定したら、保存・確定を行ったうえで
  // 警告する (複数コマは並べる)。
  const warnNegativeKomas = useCallback(
    (komas: number[]): void => {
      if (komas.length === 0) return
      appToaster.create({
        type: 'warning',
        title: formatNegativeWarning(komas, t, targetDateLabel),
      })
    },
    [t, targetDateLabel],
  )

  // 保存・「保存して移動」共通の結果処理。上限超過 (AC-DFC-EDIT-004) は先頭を title、
  // 残りを description に並べたエラー、送信後の失敗は errorTitle のエラー、負の値は
  // 警告を出す。全て成功した時だけ true を返す。
  const reportSaveResult = useCallback(
    (result: SaveAllResult, errorTitle: string): boolean => {
      if (result.kind === 'limitRejected') {
        const [title, ...rest] = formatLimitViolationLines(
          result.violations,
          t,
          targetDateLabel,
        )
        appToaster.create({
          type: 'error',
          title,
          description: rest.length > 0 ? rest.join('\n') : undefined,
        })
        return false
      }
      if (result.kind === 'notSent' || !result.ok) {
        appToaster.create({ type: 'error', title: errorTitle })
      }
      if (result.kind === 'notSent') return false
      warnNegativeKomas(result.negativeKomas)
      return result.ok
    },
    [t, targetDateLabel, warnNegativeKomas],
  )

  // AC-038/039/040/042: 「保存」。dirty な全小売分を保存し、全成功時のみ success
  // toast、1 つでも失敗すれば error toast。過去日は guardWrite が no-op にする
  // (executeSaveAll 内の gate は write seam の二重防御として残る)。KomaCard.onSave は
  // `() => void` を期待するため guardWrite の Promise を fire-and-forget で捨てる
  // (旧 index の handleSave と同型)。
  const handleSave = useCallback((): void => {
    void guardWrite(async () => {
      const ok = reportSaveResult(
        await executeSaveAll(),
        t('page:demandForecastDaily.save.error'),
      )
      if (ok) {
        showSuccessToast({
          title: t('page:demandForecastDaily.save.success'),
        })
      }
    })()
  }, [guardWrite, executeSaveAll, reportSaveResult, showSuccessToast, t])

  // AC-07: 「保存して移動」。保存成功で proceed、失敗 (上限超過を含む) は離脱を取り消して
  // 留まる。過去日は通信・トーストなしで離脱をキャンセルする (onBlocked = leaveGuard.cancel)。
  const handleSaveAndLeave = useMemo(
    () =>
      guardWrite(
        async () => {
          setIsLeaveSaving(true)
          try {
            const ok = reportSaveResult(
              await executeSaveAll(),
              t('page:demandForecastDaily.leaveGuard.saveError'),
            )
            if (ok) {
              leaveGuard.proceed()
            } else {
              leaveGuard.cancel()
            }
          } finally {
            setIsLeaveSaving(false)
          }
        },
        () => {
          leaveGuard.cancel()
        },
      ),
    [guardWrite, executeSaveAll, leaveGuard, reportSaveResult, t],
  )

  const handleDiscardAndLeave = useCallback((): void => {
    discardAllDirty()
    leaveGuard.proceed()
  }, [discardAllDirty, leaveGuard])

  // BG_TOTAL(複数小売)ビューは currentEditRow が undefined のため、conflict な
  // 全小売を一括解決する (review 指摘 PR #3199)。
  const handleResolveConflict = useCallback((): void => {
    if (targetScope !== 'BG_TOTAL' && currentEditRow !== undefined) {
      void resolveConflicts([currentEditRow.retailerBusinessCd])
      return
    }
    const conflictedRetailerBusinessCds = Object.entries(saveStates)
      .filter(([, state]) => state === 'conflict')
      .map(([retailerBusinessCd]) => retailerBusinessCd)
    if (conflictedRetailerBusinessCds.length > 0)
      void resolveConflicts(conflictedRetailerBusinessCds)
  }, [currentEditRow, resolveConflicts, saveStates, targetScope])

  // ── 小売 / scope 切替 (未保存ガード経由) ─────────────────────────────
  const onRetailerChange = useCallback(
    (retailerBusinessCd: string): void => {
      if (targetScope === 'UNATTRIBUTED') {
        setSelectedRetailerBusinessCd(retailerBusinessCd)
        setTargetScope('RETAILER')
        return
      }
      // AC-05: 未保存の変更がある状態での小売切替は 3 択ダイアログ経由。
      leaveGuard.guardAction(() => {
        setSelectedRetailerBusinessCd(retailerBusinessCd)
        setTargetScope('RETAILER')
      })
    },
    [leaveGuard, targetScope],
  )

  const onTargetScopeChange = useCallback(
    (scope: TargetScope): void => {
      // AC-DFD-047c: 帰属不明の閲覧は同じ未保存sessionを保持する。
      if (scope === 'UNATTRIBUTED' || targetScope === 'UNATTRIBUTED') {
        setTargetScope(scope)
        return
      }
      // AC-05: BG全体⇄小売切替も同様にガードする。
      leaveGuard.guardAction(() => {
        setTargetScope(scope)
      })
    },
    [leaveGuard, targetScope],
  )

  // ── 確定 ─────────────────────────────────────────────────────────
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isUnattributedGuardOpen, setIsUnattributedGuardOpen] = useState(false)
  // 確定完了 結果モーダル (design L998-1020): 確定成功後の確定時刻。undefined 以外で開く。
  const [confirmResultAt, setConfirmResultAt] = useState<Date | undefined>(
    undefined,
  )
  // ダイアログを閉じた後も残す「確定済み」持続表示用 (ConfirmBar hint)。
  const [confirmedIndicatorAt, setConfirmedIndicatorAt] = useState<
    Date | undefined
  >(undefined)
  // 編集で未保存 (dirty) になったら「確定済み」表示をクリアする (Devin review 指摘)。
  useEffect(() => {
    if (anyDirty) {
      setConfirmedIndicatorAt(undefined)
    }
  }, [anyDirty])
  // AC-DFD-051: wfCase が確定を返した後は wfCase の確定日時を出すため、確定 RPC
  // 応答の日時を手放す (差戻し・完了取消で wfCase が未確定へ戻った時に出し続けない)。
  useEffect(() => {
    if (isConfirmedOnServer && confirmedIndicatorAt !== undefined) {
      setConfirmedIndicatorAt(undefined)
    }
  }, [isConfirmedOnServer, confirmedIndicatorAt])

  const {
    mutateAsync: confirmAsync,
    isPending: isConfirming,
    error: confirmError,
    reset: resetConfirmMutation,
  } = useConfirmDemandForecast(caseId ?? '')

  const handleConfirmExecute = async (
    rationale: ConfirmRationale,
  ): Promise<void> => {
    if (
      targetScope === 'UNATTRIBUTED' ||
      selectedAreaCode === undefined ||
      targetDate === undefined
    )
      return
    // 過去日 write-seam (guardWrite と同一 predicate の inline 呼び出し): areaCode/
    // targetDate の precondition を先に評価する順序制約があるため HOF 化せずここで
    // canMutate を呼ぶ。確定は編集 hook を経由しない別 write seam のため入口に gate を
    // 残す。canMutate() は呼び出し時の実時刻で評価するため stale 化しない。ダイアログも
    // 閉じて UI と実行の乖離を防ぐ (PR #5066 review C-5)。
    if (!canMutate(targetDate)) {
      setIsConfirmOpen(false)
      return
    }
    const targets = Object.values(editRows).map((row) =>
      create(RetailerConfirmTargetSchema, {
        retailerBusinessCd: row.retailerBusinessCd,
        expectedUpdatedAt: row.draftUpdatedAt,
      }),
    )
    try {
      const res = await confirmAsync(
        create(ConfirmDemandForecastRequestSchema, {
          caseId: caseId ?? '',
          areaCode: selectedAreaCode,
          section,
          targetDate,
          targets,
          ...rationale,
        }),
      )
      setIsConfirmOpen(false)
      // AC-DFC-EDIT-005: 確定した全事業者の画面の値にある負のコマを警告する
      // (確定は止めない)。
      warnNegativeKomas(
        collectNegativeKomas(
          Object.values(editRows).map((row) => row.forecastSlots),
          gcKoma,
        ),
      )
      // 確定日時はサーバー由来 (response.confirmedAt) を使う。未設定時のみ now()。
      const confirmedAt =
        res.confirmedAt !== undefined
          ? timestampDate(res.confirmedAt)
          : serverNow()
      setConfirmResultAt(confirmedAt)
      setConfirmedIndicatorAt(confirmedAt)
    } catch (error) {
      // A concurrent allocation can leave a stale client view.  Treat the
      // backend guard exactly like the preflight guard rather than exposing a
      // second, generic confirmation error path.
      if (
        error instanceof ConnectError &&
        readAppErrorInfo(error, DemandForecastConfirmErrorReasonSchema)
          ?.reason === DemandForecastConfirmErrorReason.UNATTRIBUTED_REMAINING
      ) {
        setIsConfirmOpen(false)
        setIsUnattributedGuardOpen(true)
      }
      // AC-07: 全エラーは confirmError 経由で ConfirmDemandDialog に表示される。
    }
  }

  const openConfirmDialog = useCallback((): void => {
    if (targetScope === 'UNATTRIBUTED') return
    if (hasUnattributedRemaining) {
      setIsUnattributedGuardOpen(true)
      return
    }
    setIsConfirmOpen(true)
  }, [hasUnattributedRemaining, targetScope])

  // 閉じる時に mutation を reset (再オープン時の前回エラー残り防止)。ただし pending
  // 中 (isConfirming) の reset は observer を idle に戻して二重確定・エラー握り潰しを
  // 招くため行わない。
  const closeConfirmDialog = useCallback((): void => {
    setIsConfirmOpen(false)
    if (!isConfirming) {
      resetConfirmMutation()
    }
  }, [isConfirming, resetConfirmMutation])

  const closeConfirmResult = useCallback((): void => {
    setConfirmResultAt(undefined)
  }, [])

  // ── 最大電力補正モーダル ──────────────────────────────────────────
  const [isRescaleOpen, setIsRescaleOpen] = useState(false)
  const openRescale = useCallback((): void => {
    setIsRescaleOpen(true)
  }, [])
  const closeRescale = useCallback((): void => {
    setIsRescaleOpen(false)
  }, [])

  return {
    // selection state
    targetScope,
    selectedRetailerBusinessCd,
    dragMode,
    setDragMode,
    onRetailerChange,
    onTargetScopeChange,

    // scope / gates
    isSingleRetailerBg,
    hasMultipleRetailers,
    canMutateTargetDate,
    ...gates,

    // 配下 editor の result を単一 nested value として公開 (素通し再エクスポート廃止)。
    // index は model.editor から表示 state / buffer-write ハンドラを読む。
    editor: draftEditor,

    targetDateLabel,
    // write orchestration
    handleSave,
    handleSaveAndLeave,
    handleDiscardAndLeave,
    handleResolveConflict,
    handleConfirmExecute,

    // leave guard
    leaveGuardBlocked: leaveGuard.isBlocked,
    leaveGuardCancel: leaveGuard.cancel,
    isLeaveSaving,

    // confirm dialog + result
    isConfirmOpen,
    openConfirmDialog,
    closeConfirmDialog,
    isConfirming,
    confirmError,
    confirmResultAt,
    closeConfirmResult,
    confirmedIndicatorAt,
    hasUnattributedRemaining,
    isUnattributedGuardOpen,
    closeUnattributedGuard: () => setIsUnattributedGuardOpen(false),

    // rescale modal
    isRescaleOpen,
    openRescale,
    closeRescale,
  }
}
